/** Body behaviours (moving / fade away / rotating / player controlled / static) and level <-> body conversion. */
import type { Draft } from 'immer';
import type { Body, BodyBehavior, CustomComponent, LevelDoc, PlacedObject, TerrainShape, TileLayer, Vec2 } from '../model/format.ts';
import { gameData } from '../data/gameData.ts';
import { uid, useEditor } from './store.ts';
import { defaultMoving, newLayer, stripRefs } from './actions.ts';
import { pointInTerrain, ringBounds } from './polygon.ts';
import { setCells } from './tileOps.ts';
import { bodyBounds } from './scene.ts';
import { settings } from './settings.ts';
import { FOLIAGE_REACH } from './grass.ts';

export const BEHAVIOR_LABELS: [BodyBehavior, string][] = [
  ['movingPlatform', 'Moving platform'],
  ['fadeOut', 'Fades away'],
  ['rotating', 'Rotating'],
  ['controlled', 'Player controlled'],
  ['static', 'Static block'],
];

export function behaviorLabel(b: BodyBehavior): string {
  return BEHAVIOR_LABELS.find(([k]) => k === b)?.[1] ?? b;
}

const BEHAVIOR_NAMES: Record<BodyBehavior, string> = { movingPlatform: 'Moving Platform', fadeOut: 'Fade Out Block', rotating: 'Rotating Block', controlled: 'Controlled Block', static: 'Block' };

const BEHAVIOR_COMP: Partial<Record<BodyBehavior, string>> = { fadeOut: 'FadeOutHandler', rotating: 'RigidBodyRotator' };
const BEHAVIOR_TYPES = Object.values(BEHAVIOR_COMP);

/** Serialized data of a component type cloned from the first prefab that has one (scene references cleared). */
export function defaultComponentData(type: string): Record<string, any> {
  const gd = gameData();
  for (const p of Object.values(gd.prefabs)) {
    for (const n of Object.values(p.nodes)) {
      if (n.type !== type) continue;
      const clean = (v: any): any => {
        if (Array.isArray(v)) return v.map(clean);
        if (!v || typeof v !== 'object') return v;
        if ('fileID' in v) return v.guid ? { ...v } : { fileID: '0' };
        const o: any = {};
        for (const [k, x] of Object.entries(v)) o[k] = clean(x);
        return o;
      };
      const data = clean(n.data);
      const s = (n.data as any).m_Script;
      if (s?.guid) data.m_Script = { asset: `${s.guid}:${s.fileID ?? '11500000'}` };
      return data;
    }
  }
  return { m_Enabled: '1' };
}

function rigidbodyData(bodyType: number): Record<string, any> {
  return { ...defaultComponentData('Rigidbody2D'), m_BodyType: String(bodyType) };
}

/** Give a (draft) body the components its behaviour needs and drop the ones of other behaviours. */
export function applyBehavior(b: Draft<Body> | Body, behavior: BodyBehavior) {
  const prev = b.behavior;
  b.behavior = behavior;
  if (prev !== behavior && !b.owner && b.prefab) {
    delete b.prefab;
    b.mods = [];
    for (const l of b.layers) delete l.unitySourceId;
  }
  const want = BEHAVIOR_COMP[behavior];
  const drop = (list: CustomComponent[] | undefined) => list?.filter((c) => !BEHAVIOR_TYPES.includes(c.type) || c.type === want);
  b.components = drop(b.components as CustomComponent[]);
  for (const l of b.layers) {
    l.extraComponents = drop(l.extraComponents as CustomComponent[]);
    if (!l.extraComponents?.length) delete l.extraComponents;
  }
  if (behavior === 'movingPlatform') {
    if (!b.moving) {
      const o = b.origin;
      b.moving = defaultMoving([o[0], o[1]], [o[0], o[1] + 3]);
    }
    if (!b.components?.length) delete b.components;
    delete b.rigidbody;
    return;
  }
  const comps = (b.components ??= []) as CustomComponent[];
  const hasCopy = (t: string) => comps.some((c) => c.type === t) || b.layers.some((l) => l.extraComponents?.some((c) => c.type === t));
  if (want && !hasCopy(want)) {
    const data = defaultComponentData(want);
    if (want === 'RigidBodyRotator') {
      data.activator = { fileID: '0' };
      data.pivot = { fileID: '0' };
    }
    comps.unshift({ type: want, data });
  }
  const bodyType = behavior === 'static' ? 2 : 1;
  const rb = comps.find((c) => c.type === 'Rigidbody2D');
  if (rb) rb.data.m_BodyType = String(bodyType);
  else comps.push({ type: 'Rigidbody2D', data: rigidbodyData(bodyType) });
  if (behavior === 'static') b.rigidbody = { bodyType: 2 };
  else delete b.rigidbody;
}

export function setBehavior(bodyId: string, behavior: BodyBehavior) {
  const st = useEditor.getState();
  let needsController = false;
  let leftControls = false;
  st.change('Change body type', (d) => {
    const b = d.bodies.find((x) => x.id === bodyId);
    if (!b) return;
    leftControls = b.behavior === 'controlled' && behavior !== 'controlled' && hasController(d as LevelDoc, bodyId);
    if (!b.owner && b.name === BEHAVIOR_NAMES[b.behavior]) b.name = BEHAVIOR_NAMES[behavior];
    applyBehavior(b, behavior);
    needsController = behavior === 'controlled' && !hasController(d as LevelDoc, bodyId);
  });
  if (needsController) addController(bodyId);
  else if (leftControls) st.notify('Its controller and control panel are still in the level; delete them if you no longer need them');
}

function hasController(doc: LevelDoc, bodyId: string): boolean {
  const ref = `body:${bodyId}`;
  for (const o of [...doc.objects, ...doc.bodies.flatMap((b) => b.objects)]) {
    for (const c of o.components ?? []) if (c.type === 'BodyControlHandler' && JSON.stringify(c.data.uiRigidBodiesOrGameObjects ?? []).includes(`"${ref}"`)) return true;
    if (o.mods.some((m) => m.r === ref)) return true;
  }
  return false;
}

const CONTROL_SWITCH = 'b768bfb05c57b214b8d3a03d506510b8';

/** A controller object (BodyControlHandler) plus a control panel next to the body, steering it left and right. */
function makeController(d: Draft<LevelDoc>, b: Draft<Body> | Body, bb: [number, number, number, number] | null): string {
  const box = bb ?? [0, 0, 1, 1];
  const left = b.origin[0] + box[0], top = b.origin[1] + box[3], cx = b.origin[0] + (box[0] + box[2]) / 2;
  const panelId = uid('o');
  const ctrlId = uid('o');
  const panel: PlacedObject = { id: panelId, name: 'Control Panel', prefab: gameData().prefabs[CONTROL_SWITCH] ? CONTROL_SWITCH : null, x: Math.round(left) - 1.5, y: top + 0.5, rotation: 0, scale: [1, 1], mods: [] };
  if (!panel.prefab) panel.components = [{ type: 'BodyControlPanel', data: defaultComponentData('BodyControlPanel') }];
  const data = defaultComponentData('BodyControlHandler');
  Object.assign(data, {
    controlSpeed: '0.07',
    controlAcceleration: '0.002',
    uiRigidBodiesOrGameObjects: [{ rigidbodyOrParent: { fileID: '1', ref: `body:${b.id}` }, xAxisControls: '1', xInputAxisScale: '1', yAxisControls: '0', yInputAxisScale: '1' }],
    uiControlPanels: [{ fileID: '1', ref: `obj:${panelId}#BodyControlPanel` }],
    leftControlStart: { x: '0', y: '0' },
    rightControlStart: { x: '0', y: '0' },
    xMin: '-5', xMax: '5', yMin: '0', yMax: '0',
  });
  const ctrl: PlacedObject = { id: ctrlId, name: `${b.name} Controller`, prefab: null, x: Math.round(cx), y: top + 2, rotation: 0, scale: [1, 1], mods: [], components: [{ type: 'BodyControlHandler', data }] };
  d.objects.push(panel, ctrl);
  return ctrlId;
}

export function addController(bodyId: string): string | null {
  const st = useEditor.getState();
  const doc = st.doc;
  const b = doc?.bodies.find((x) => x.id === bodyId);
  if (!doc || !b) return null;
  const bb = bodyBounds(doc, b);
  let id: string | null = null;
  st.change('Add body controller', (d) => {
    const db = d.bodies.find((x) => x.id === bodyId);
    if (db) id = makeController(d, db, bb);
  });
  return id;
}

// ---------- level shape <-> body ----------

const shift = (ring: Vec2[], dx: number, dy: number): Vec2[] => ring.map(([x, y]) => [x + dx, y + dy] as Vec2);

function overlaps(a: [number, number, number, number], b: [number, number, number, number]) {
  return a[0] < b[2] && b[0] < a[2] && a[1] < b[3] && b[1] < a[3];
}

/** Any quarter of the cell inside the shape. */
function cellTouches(t: TerrainShape, x: number, y: number) {
  return [[0.25, 0.25], [0.75, 0.25], [0.25, 0.75], [0.75, 0.75], [0.5, 0.5]].some(([qx, qy]) => pointInTerrain(t, x + qx, y + qy));
}

/** Cells between a cell and a box [x0, y0, x1, y1) (0 inside). */
function boxDistance([x0, y0, x1, y1]: [number, number, number, number], x: number, y: number) {
  return Math.max(x0 - x, x - (x1 - 1), y0 - y, y - (y1 - 1), 0);
}

/** Split stride-3 paint into the cells that go with a shape (moved to body-local cells) and the rest. */
function splitPaint(paint: number[], take: (x: number, y: number) => boolean, origin: Vec2): { keep: number[]; moved: number[] } {
  const keep: number[] = [];
  const moved: number[] = [];
  for (let i = 0; i + 2 < paint.length; i += 3) {
    const [x, y, v] = [paint[i], paint[i + 1], paint[i + 2]];
    if (take(x, y)) moved.push(x - origin[0], y - origin[1], v);
    else keep.push(x, y, v);
  }
  return { keep, moved };
}

/** Merge stride-3 paint shifted by (ox, oy) into another (later cells win). */
function mergePaint(into: number[] | undefined, add: number[], ox: number, oy: number): number[] {
  const m = new Map<string, [number, number, number]>();
  const lp = into ?? [];
  for (let i = 0; i + 2 < lp.length; i += 3) m.set(`${lp[i]},${lp[i + 1]}`, [lp[i], lp[i + 1], lp[i + 2]]);
  for (let i = 0; i + 2 < add.length; i += 3) {
    const x = add[i] + ox, y = add[i + 1] + oy;
    m.set(`${x},${y}`, [x, y, add[i + 2]]);
  }
  return [...m.values()].flat();
}

/**
 * Turn a level terrain shape into a new body: the shape (still a polygon, auto-tiled), the cuts that overlap it,
 * its hand-placed tiles, its painted grass and foliage and (optionally) the objects standing on it move into the body.
 */
export function shapeToBody(terrainId: string, behavior: BodyBehavior = 'movingPlatform', takeObjects = settings().convertTakesObjects): string | null {
  const st = useEditor.getState();
  const doc = st.doc;
  if (!doc) return null;
  const t = doc.terrains.find((x) => x.id === terrainId);
  if (!t) {
    st.notify('Only level shapes can become moving platforms');
    return null;
  }
  if (t.mode !== 'add') {
    st.notify('Pick a shape that adds terrain, not a cut');
    return null;
  }
  const tb = ringBounds(t.rings[0]);
  const origin: Vec2 = [Math.round((tb[0] + tb[2]) / 2), Math.round((tb[1] + tb[3]) / 2)];
  const id = uid('b');
  let controller = false;
  st.change(`Make ${behaviorLabel(behavior).toLowerCase()}`, (d) => {
    const layerMap = new Map<string, TileLayer>();
    const bodyLayers: TileLayer[] = [];
    for (const role of ['level', 'foreground', 'background'] as const) {
      const src = d.layers.find((l) => l.id === t.targets[role]);
      if (!src) continue;
      const nl: TileLayer = { ...newLayer(role, true, src.name), ...(src.color ? { color: [...src.color] as TileLayer['color'] } : {}), ...(src.oneWayGenerator ? { oneWayGenerator: true } : {}) };
      layerMap.set(src.id, nl);
      bodyLayers.push(nl);
    }
    {
      const src = d.layers.find((l) => l.role === 'spikes');
      if (src) {
        const nl: TileLayer = { ...newLayer('spikes', true, src.name), ...(src.color ? { color: [...src.color] as TileLayer['color'] } : {}) };
        layerMap.set(src.id, nl);
        bodyLayers.push(nl);
      }
    }
    const retarget = (x: TerrainShape): TerrainShape => {
      const c: TerrainShape = JSON.parse(JSON.stringify(x));
      c.id = x === t ? x.id : uid('t');
      c.rings = c.rings.map((r) => shift(r, -origin[0], -origin[1]));
      c.targets = {
        level: layerMap.get(x.targets.level)?.id ?? bodyLayers[0].id,
        foreground: x.targets.foreground ? layerMap.get(x.targets.foreground)?.id : undefined,
        background: x.targets.background ? layerMap.get(x.targets.background)?.id : undefined,
      };
      return c;
    };
    const terrains: TerrainShape[] = [retarget(t)];
    const idx = d.terrains.findIndex((x) => x.id === t.id);
    d.terrains.slice(idx + 1).forEach((c) => {
      if (c.mode === 'subtract' && !c.hidden && c.targets.level === t.targets.level && overlaps(ringBounds(c.rings[0]), tb)) terrains.push(retarget(c as TerrainShape));
    });
    // hand-placed tiles and erase markers inside the shape
    for (const [srcId, nl] of layerMap) {
      const src = d.layers.find((l) => l.id === srcId)!;
      const keep: number[] = [];
      const moved: number[] = [];
      for (let i = 0; i < src.tiles.length; i += 4) {
        const x = src.tiles[i], y = src.tiles[i + 1];
        if (pointInTerrain(t, x + 0.5, y + 0.5)) moved.push(x - origin[0], y - origin[1], src.tiles[i + 2], src.tiles[i + 3]);
        else keep.push(x, y, src.tiles[i + 2], src.tiles[i + 3]);
      }
      if (moved.length) {
        src.tiles = keep;
        nl.tiles = moved;
      }
    }
    let grass: number[] | undefined;
    if (d.grassPaint?.length) {
      const { keep, moved } = splitPaint(d.grassPaint, (x, y) => cellTouches(t, x, y), origin);
      if (moved.length) {
        grass = moved;
        if (keep.length) d.grassPaint = keep;
        else delete d.grassPaint;
      }
    }
    let grassLines: number[] | undefined;
    if (d.grassLines?.length) {
      const { keep, moved } = splitPaint(d.grassLines, (x, y) => cellTouches(t, x, y), origin);
      if (moved.length) {
        grassLines = moved;
        if (keep.length) d.grassLines = keep;
        else delete d.grassLines;
      }
    }
    let spikeLines: number[] | undefined;
    if (d.spikeLines?.length) {
      const { keep, moved } = splitPaint(d.spikeLines, (x, y) => cellTouches(t, x, y), origin);
      if (moved.length) {
        spikeLines = moved;
        if (keep.length) d.spikeLines = keep;
        else delete d.spikeLines;
      }
    }
    // foliage paint reaches past the shape: it goes with the shape it is closest to
    let foliage: number[] | undefined;
    if (d.decoPaint?.length) {
      const others = d.terrains.filter((x) => x.id !== t.id && x.mode === 'add' && !x.hidden && x.rings.length).map((x) => ringBounds(x.rings[0]));
      const take = (x: number, y: number) => {
        const own = boxDistance(tb, x, y);
        if (own > FOLIAGE_REACH) return false;
        return cellTouches(t, x, y) || others.every((b) => boxDistance(b, x, y) > own);
      };
      const { keep, moved } = splitPaint(d.decoPaint, take, origin);
      if (moved.length) {
        foliage = moved;
        if (keep.length) d.decoPaint = keep;
        else delete d.decoPaint;
      }
    }
    const owners = new Set(d.bodies.map((b) => b.owner?.object).filter(Boolean));
    const systemIds = new Set(Object.values(d.system));
    const riders = takeObjects
      ? d.objects.filter((o) => !o.attach && !owners.has(o.id) && !systemIds.has(o.id) && (pointInTerrain(t, o.x, o.y) || pointInTerrain(t, o.x, o.y - 0.75)))
      : [];
    const riderIds = new Set(riders.map((o) => o.id));
    if (spikeLines?.length && !bodyLayers.some((l) => l.role === 'spikes')) bodyLayers.push(newLayer('spikes', true));
    const body: Body = {
      id,
      name: t.name ?? BEHAVIOR_NAMES[behavior],
      behavior: 'static',
      placement: 'behindLevel',
      origin,
      layers: bodyLayers,
      terrains,
      ...(grass ? { grassPaint: grass } : {}),
      ...(grassLines ? { grassLines } : {}),
      ...(spikeLines ? { spikeLines } : {}),
      ...(foliage ? { decoPaint: foliage } : {}),
      objects: JSON.parse(JSON.stringify(riders)),
      mods: [],
    };
    applyBehavior(body, behavior);
    d.objects = d.objects.filter((o) => !riderIds.has(o.id));
    d.terrains = d.terrains.filter((x) => x.id !== t.id);
    d.bodies.push(body);
    if (behavior === 'controlled') {
      const lb: [number, number, number, number] = [tb[0] - origin[0], tb[1] - origin[1], tb[2] - origin[0], tb[3] - origin[1]];
      makeController(d, body, lb);
      controller = true;
    }
  });
  st.set({ selection: { kind: 'body', id } });
  st.notify(controller ? 'Made a player-controlled block with a control panel' : `Made a ${behaviorLabel(behavior).toLowerCase()}`);
  return id;
}

/** Move a body's terrain shapes, hand-placed tiles, painted grass / foliage and riding objects back into the level and delete the body. */
export function bodyToLevel(bodyId: string): string | null {
  const st = useEditor.getState();
  const doc = st.doc;
  const b = doc?.bodies.find((x) => x.id === bodyId);
  if (!doc || !b) return null;
  if (b.owner) {
    st.notify('This body belongs to a prefab (e.g. a door) and cannot be merged into the level');
    return null;
  }
  const [ox, oy] = b.origin;
  let first: string | null = null;
  st.change('Move back to level', (d) => {
    const db = d.bodies.find((x) => x.id === bodyId)!;
    const layerMap = new Map<string, string>();
    for (const l of db.layers) {
      let target = d.layers.find((x) => x.role === l.role && !x.locked) ?? d.layers.find((x) => x.role === l.role);
      if (!target) {
        target = newLayer(l.role, false, l.name);
        d.layers.push(target);
      }
      layerMap.set(l.id, target.id);
      if (l.tiles.length) {
        const cells: { x: number; y: number; p: number; f: number }[] = [];
        for (let i = 0; i < l.tiles.length; i += 4) cells.push({ x: l.tiles[i] + ox, y: l.tiles[i + 1] + oy, p: l.tiles[i + 2], f: l.tiles[i + 3] });
        target.tiles = setCells(target.tiles as number[], cells);
      }
    }
    for (const t of db.terrains) {
      const c: TerrainShape = JSON.parse(JSON.stringify(t));
      c.rings = c.rings.map((r) => shift(r, ox, oy));
      const lvl = layerMap.get(t.targets.level) ?? d.layers.find((x) => x.role === 'level')?.id;
      if (!lvl) continue;
      c.targets = { level: lvl, foreground: t.targets.foreground ? layerMap.get(t.targets.foreground) : undefined, background: t.targets.background ? layerMap.get(t.targets.background) : undefined };
      d.terrains.push(c);
      first ??= c.id;
    }
    if (db.grassPaint?.length) d.grassPaint = mergePaint(d.grassPaint, db.grassPaint, ox, oy);
    if (db.grassLines?.length) d.grassLines = mergePaint(d.grassLines, db.grassLines, ox, oy);
    if (db.spikeLines?.length) d.spikeLines = mergePaint(d.spikeLines, db.spikeLines, ox, oy);
    if (db.decoPaint?.length) d.decoPaint = mergePaint(d.decoPaint, db.decoPaint, ox, oy);
    d.objects.push(...(db.objects as PlacedObject[]));
    d.bodies = d.bodies.filter((x) => x.id !== bodyId);
    stripRefs(d, `body:${bodyId}`);
  });
  if (st.container === bodyId) st.set({ container: 'level' });
  st.set({ selection: first ? { kind: 'terrain', id: first } : { kind: 'none' } });
  if (b.rotation) st.notify('Moved back to the level (its rotation was dropped)');
  return first;
}

/** Move a body's origin (its rotation pivot) to the centre of its tiles without moving the content. */
export function recentreBody(bodyId: string) {
  const st = useEditor.getState();
  const doc = st.doc;
  const b = doc?.bodies.find((x) => x.id === bodyId);
  if (!doc || !b) return;
  const bb = bodyBounds(doc, b);
  if (!bb) return;
  const cx = Math.round((bb[0] + bb[2]) / 2), cy = Math.round((bb[1] + bb[3]) / 2);
  if (!cx && !cy) {
    st.notify('The pivot is already at the centre');
    return;
  }
  st.change('Centre pivot', (d) => {
    const db = d.bodies.find((x) => x.id === bodyId)!;
    db.origin = [db.origin[0] + cx, db.origin[1] + cy];
    for (const t of db.terrains) t.rings = t.rings.map((r) => shift(r as Vec2[], -cx, -cy));
    for (const l of db.layers) {
      const out: number[] = [];
      for (let i = 0; i < l.tiles.length; i += 4) out.push(l.tiles[i] - cx, l.tiles[i + 1] - cy, l.tiles[i + 2], l.tiles[i + 3]);
      l.tiles = out;
    }
    for (const g of [db.grassPaint, db.grassLines, db.spikeLines, db.decoPaint]) {
      if (g) for (let i = 0; i + 2 < g.length; i += 3) { g[i] -= cx; g[i + 1] -= cy; }
    }
    if (db.moving) for (const w of db.moving.waypoints) { w.x += cx; w.y += cy; }
  });
}

/** Which level shape (topmost first) contains a world point. */
export function levelShapeAt(doc: LevelDoc, x: number, y: number): TerrainShape | null {
  return [...doc.terrains].reverse().find((t) => !t.hidden && t.mode === 'add' && pointInTerrain(t, x, y)) ?? null;
}
